import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Download, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Calendar, Filter } from 'lucide-react';
import axios from 'axios';
import { useSelector } from "react-redux";
import { exportCostExplorerDataToCsv } from '../CostExplorerExport';
import CustomDateRangePicker from './CustomDateRangePicker';
import { jwtDecode } from "jwt-decode";

const PRIMARY_COLOR = '#003366';


const AllEpDataGraph = () => {
  const [tempEpSelections, setTempEpSelections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [epNoData, setEpNoData] = useState([]);
  const [selectedEpNos, setSelectedEpNos] = useState([]);
  const [showEpDropdown, setShowEpDropdown] = useState(false);
  const [dateRange, setDateRange] = useState('1month');
  const [customStartDate, setCustomStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [customEndDate, setCustomEndDate] = useState(() => new Date());
  const [costData, setCostData] = useState({
    dimensionGraphData: [],
    totalCost: 0,
    epBreakdown: [],
    accountBreakdown: {}
  });
  const epDataAbortControllerRef = useRef(null);
  const costDataAbortControllerRef = useRef(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [dataReady, setDataReady] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const epDropdownRef = useRef(null);
  // Track if this is the initial load
  const initialLoadRef = useRef(true);
  // Store selected EPs between date changes
  const previousSelectionsRef = useRef([]);
  // Track last applied EP filter for animation purposes
  const lastAppliedFilterRef = useRef([]);

  // Get these values from environment or Redux store
  const apiUrl = process.env.REACT_APP_API_URL;
  const accessToken = useSelector((state) => state.accessToken.value);
  const idToken = useSelector((state) => state.idToken.value);

  // Primary color
  const PRIMARY_COLOR = '#003366';

  // Color palette for charts
  const COLORS = [
    '#FF9900', '#1E88E5', '#7E57C2', '#26A69A', '#43A047',
    '#5E35B1', '#FB8C00', '#F4511E', '#6D4C41', '#546E7A'
  ];
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (epDropdownRef.current && !epDropdownRef.current.contains(event.target)) {
        setShowEpDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    return () => {
      if (epDataAbortControllerRef.current) {
        epDataAbortControllerRef.current.abort();
      }
      if (costDataAbortControllerRef.current) {
        costDataAbortControllerRef.current.abort();
      }
    };
  }, []);
  // Define date filters based on the selected range
  const getDateFilters = () => {
    const now = new Date();
    let startDate;
    let endDate;

    switch (dateRange) {
      case "1month":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now);
        break;
      case "3months":
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case "6months":
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case "1year":
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case "custom":
        if (customStartDate && customEndDate) {
          startDate = customStartDate;
          endDate = customEndDate;
        } else {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now);
        }
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now);
    }


    // Format dates to YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Fetch all EP No data
  const fetchEpNoData = async () => {
    // Cancel previous request if it exists
    if (epDataAbortControllerRef.current) {
      epDataAbortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    epDataAbortControllerRef.current = controller;

    if (initialLoadRef.current) {
      setLoading(true);
    } else {
      setIsUpdating(true);
    }
    setDataReady(false);

    let userEmail = "";
    try {
      const decoded = jwtDecode(idToken);
      userEmail = decoded.email || "";
    } catch (err) {
      console.error("Error decoding token:", err);
    }

    try {
      const epQuery = `
      query GetUserEps($email: String!) {
        getUserAssociatedEps(email: $email) {
          EpNo
          Oem
        }
      }
    `;

      const response = await axios.post(
        apiUrl,
        {
          query: epQuery,
          variables: { email: userEmail }
        },
        {
          headers: {
            Authorization: accessToken,
          },
          signal: controller.signal // Add abort signal
        }
      );


      if (response.data.errors && response.data.errors[0].errorType === "Unauthorized") {
        setError("Unauthorized");
      } else if (response.data.data && response.data.data.getUserAssociatedEps) {
        const userAssociatedEps = response.data.data.getUserAssociatedEps || [];

        setEpNoData(userAssociatedEps);

        let epsToUse;
        const allUserEpNos = userAssociatedEps.map(ep => ep.EpNo);

        if (initialLoadRef.current) {
          epsToUse = allUserEpNos;
          initialLoadRef.current = false;
        } else if (previousSelectionsRef.current.length > 0) {
          epsToUse = previousSelectionsRef.current.filter(ep => allUserEpNos.includes(ep));

          if (epsToUse.length === 0) {
            epsToUse = allUserEpNos;
          }
        } else {
          epsToUse = selectedEpNos.length > 0 ? selectedEpNos.filter(ep => allUserEpNos.includes(ep)) : allUserEpNos;
        }

        setSelectedEpNos(epsToUse);
        if (epsToUse.length > 0) {
          fetchAggregatedCostData(epsToUse);
        } else {
          setLoading(false);
          setIsUpdating(false);
          setDataReady(true);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('Request was cancelled');
        return;
      }

      console.error("Error fetching EP No data:", error);
      setToast({
        type: 'error',
        message: 'Failed to fetch EP No data'
      });
      setLoading(false);
      setIsUpdating(false);
      setDataReady(false);
    }
  };
  // Prepare export data for CSV
  const prepareExportData = () => {
    if (!costData.epBreakdown || costData.epBreakdown.length === 0) {
      return { headers: [], rows: [] };
    }
    const headers = ["EP No", "OEM", "Total Cost"];
    const rows = costData.epBreakdown.map(ep => {
      const epData = epNoData.find(item => item.EpNo === ep.epNo);
      const oem = epData?.Oem || "N/A";

      return {
        "EP No": `${ep.epNo}`,
        "OEM": oem,
        "Total Cost": formatCurrency(ep.totalCost)
      };
    });

    return { headers, rows };
  };


  const fetchAggregatedCostData = async (epNumbers) => {
    // Cancel previous request if it exists
    if (costDataAbortControllerRef.current) {
      costDataAbortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    costDataAbortControllerRef.current = controller;

    try {
      setIsUpdating(true);
      setDataReady(false);

      const { startDate, endDate } = getDateFilters();
      lastAppliedFilterRef.current = [...epNumbers];

      const accountQuery = `
      query MyQuery {
        getAWSAccounts {
          AccountName
          AccountNo
          id
          EpNo
        }
      }
    `;

      const accountMappingResponse = await axios.post(
        apiUrl,
        { query: accountQuery },
        {
          headers: {
            Authorization: accessToken,
          },
          signal: controller.signal // Add abort signal
        }
      );

      if (accountMappingResponse.data.errors &&
        accountMappingResponse.data.errors[0].errorType === "Unauthorized") {
        throw new Error("Unauthorized");
      }

      const allAwsAccounts = accountMappingResponse.data.data?.getAWSAccounts || [];
      const filteredAccounts = allAwsAccounts.filter(account =>
        epNumbers.includes(account.EpNo)
      );

      const accountNoToName = {};
      const epAccountsMap = {};
      const allAccountsSet = new Set();

      filteredAccounts.forEach(account => {
        if (!accountNoToName[account.AccountNo]) {
          accountNoToName[account.AccountNo] = [];
        }
        if (!accountNoToName[account.AccountNo].includes(account.AccountName)) {
          accountNoToName[account.AccountNo].push(account.AccountName);
        }

        if (!epAccountsMap[account.EpNo]) {
          epAccountsMap[account.EpNo] = [];
        }
        epAccountsMap[account.EpNo].push({
          name: account.AccountName,
          number: account.AccountNo
        });

        allAccountsSet.add(account.AccountName);
      });

      Object.keys(accountNoToName).forEach(accountNo => {
        if (accountNoToName[accountNo].length > 1) {
          accountNoToName[accountNo] = accountNoToName[accountNo].join(' / ');
        } else {
          accountNoToName[accountNo] = accountNoToName[accountNo][0];
        }
      });

      const allAccountNames = Array.from(allAccountsSet);

      if (allAccountNames.length === 0) {
        setCostData({
          dimensionGraphData: [],
          totalCost: 0,
          epBreakdown: [],
          accountBreakdown: {}
        });
        setLoading(false);
        setIsUpdating(false);
        setDataReady(true);
        return;
      }

      const costQuery = `
      query GetCostData($filters: CostFiltersInput!) {
        getMainCostPageDetails(filters: $filters) {
          dimensionGraphData {
            groupByDimension
            monthOrDate
            totalCost
            year
          }
          totalCost
        }
      }
    `;

      const costVariables = {
        filters: {
          accountName: allAccountNames,
          regions: [],
          services: [],
          groupBy: "account",
          granularity: "monthly",
          startDate,
          endDate
        }
      };

      const costResponse = await axios.post(
        apiUrl,
        { query: costQuery, variables: costVariables },
        {
          headers: {
            Authorization: accessToken,
          },
          signal: controller.signal // Add abort signal
        }
      );

      if (costResponse.data.errors &&
        costResponse.data.errors[0].errorType === "Unauthorized") {
        throw new Error("Unauthorized");
      }

      const costData = costResponse.data.data?.getMainCostPageDetails || {
        dimensionGraphData: [],
        totalCost: 0
      };

      const accountTotalCosts = {};
      if (costData.dimensionGraphData && costData.dimensionGraphData.length > 0) {
        costData.dimensionGraphData.forEach(item => {
          const accountNo = item.groupByDimension;
          accountTotalCosts[accountNo] = (accountTotalCosts[accountNo] || 0) + (item.totalCost || 0);
        });
      }

      const epCostsArray = epNumbers.map(epNo => {
        const accounts = epAccountsMap[epNo] || [];
        const accountCosts = {};
        let epTotalCost = 0;

        const uniqueAccountNos = [...new Set(accounts.map(account => account.number))];

        uniqueAccountNos.forEach(accountNo => {
          const cost = accountTotalCosts[accountNo] || 0;
          const accountCostForEp = cost;

          const displayName = accountNoToName[accountNo] || accountNo;
          accountCosts[displayName] = accountCostForEp;
          epTotalCost += accountCostForEp;
        });

        return {
          epNo,
          data: {
            totalCost: epTotalCost
          },
          accounts: accounts.map(acc => accountNoToName[acc.number] || acc.name),
          accountCosts
        };
      });

      const aggregatedData = aggregateCostData(epCostsArray);
      setCostData(aggregatedData);

      setTimeout(() => {
        setLoading(false);
        setIsUpdating(false);
        setDataReady(true);
      }, 100);

    } catch (error) {
      // Don't show error if request was aborted
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('Request was cancelled');
        return;
      }

      console.error("Error fetching aggregated cost data:", error);
      if (error.message === "Unauthorized") {
        setError("Unauthorized");
      } else {
        setToast({
          type: "error",
          message: "Failed to fetch cost data"
        });
      }
      setLoading(false);
      setIsUpdating(false);
      setDataReady(false);
    }
  };


  // Aggregate cost data across multiple EP numbers
  const aggregateCostData = (epCostsArray) => {
    // Initialize aggregated data structure
    const aggregated = {
      dimensionGraphData: [],
      totalCost: 0,
      epBreakdown: [],
      accountBreakdown: {}
    };

    // Process each EP's cost data
    epCostsArray.forEach(({ epNo, data, accountCosts }) => {
      // Add to total cost
      const epTotalCost = data.totalCost || 0;
      aggregated.totalCost += epTotalCost;

      // Create EP breakdown with proper account costs
      const epBreakdownItem = {
        epNo,
        totalCost: epTotalCost,
        accountCosts: { ...accountCosts } // Clone accountCosts to avoid reference issues
      };

      aggregated.epBreakdown.push(epBreakdownItem);

      // Update global account breakdown
      if (accountCosts) {
        Object.entries(accountCosts).forEach(([account, cost]) => {
          if (!aggregated.accountBreakdown[account]) {
            aggregated.accountBreakdown[account] = 0;
          }
          aggregated.accountBreakdown[account] += cost;
        });
      }
    });

    // Convert account breakdown to dimensionGraphData format for pie chart
    aggregated.dimensionGraphData = Object.entries(aggregated.accountBreakdown).map(([account, cost]) => ({
      groupByDimension: account,
      totalCost: cost
    }));

    // Sort EP breakdown by cost (highest first)
    aggregated.epBreakdown.sort((a, b) => b.totalCost - a.totalCost);

    return aggregated;
  };

  // Prepare EP breakdown data for chart with account cost details
  const prepareEpBreakdownData = () => {
    if (!costData.epBreakdown || costData.epBreakdown.length === 0) {
      return { data: [], accounts: [] };
    }

    // Get unique account names across all EPs
    const allAccounts = new Set();
    costData.epBreakdown.forEach(ep => {
      if (ep.accountCosts) {
        Object.keys(ep.accountCosts).forEach(account => allAccounts.add(account));
      }
    });

    const uniqueAccounts = Array.from(allAccounts);
    const data = costData.epBreakdown
      .slice(0, 10) // Top 10 EPs by cost
      .map(ep => {
        const dataPoint = {
          epNo: ep.epNo,
          totalCost: ep.totalCost
        };

        // Add account costs as separate properties
        uniqueAccounts.forEach(account => {
          dataPoint[account] = ep.accountCosts?.[account] || 0;
        });

        return dataPoint;
      });
    return { data, accounts: uniqueAccounts };
  };

  // Prepare data for EP distribution pie chart
  const preparePieChartData = () => {
    if (!costData.epBreakdown || costData.epBreakdown.length === 0) {
      return [];
    }
    return costData.epBreakdown
      .filter(ep => ep.totalCost > 0)
      .map(ep => ({
        name: `EP ${ep.epNo}`,
        value: ep.totalCost,
        percentage: Math.round((ep.totalCost / costData.totalCost) * 100)
      }));
  };

  // Handle date range change
  const handleDateRangeChange = (newRange) => {
    // Store current EP selections in ref before changing date range
    previousSelectionsRef.current = [...selectedEpNos];
    setDateRange(newRange);

  };

  // Effect to fetch EP data when component mounts or date range changes
  useEffect(() => {
    fetchEpNoData();
  }, [dateRange, customEndDate, customStartDate]);

  // Handle EP selection/deselection
  const toggleEpSelection = (epNo) => {
    setTempEpSelections(prev => {
      if (prev.includes(epNo)) {
        return prev.filter(ep => ep !== epNo);
      } else {
        return [...prev, epNo];
      }
    });
  };

  // Apply EP filter changes
  const applyEpFilter = () => {
    setShowEpDropdown(false);
    const selectionsChanged =
      tempEpSelections.length !== selectedEpNos.length ||
      tempEpSelections.some(ep => !selectedEpNos.includes(ep));

    if (selectionsChanged) {
      setSelectedEpNos([...tempEpSelections]);
      previousSelectionsRef.current = [...tempEpSelections];
      fetchAggregatedCostData(tempEpSelections);
    }
  };
  const toggleEpDropdown = () => {
    if (!showEpDropdown) {
      setTempEpSelections([...selectedEpNos]);
    }
    setShowEpDropdown(!showEpDropdown);
  };

  // Handle select all EPs
  const handleSelectAllEps = () => {
    const allEps = epNoData.map(ep => ep.EpNo);
    if (tempEpSelections.length === allEps.length) {
      setTempEpSelections([]);
    } else {
      setTempEpSelections(allEps);
    }
  };
  // Handle deselect all EPs
  const handleDeselectAllEps = () => {
    // Keep at least one EP selected to avoid empty charts
    if (epNoData.length > 0) {
      setSelectedEpNos([epNoData[0].EpNo]);
    } else {
      setSelectedEpNos([]);
    }
  };

  const getCustomButtonText = () => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      const start = customStartDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
      const end = customEndDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
      return `${start} - ${end}`;
    }
    return 'Custom';
  };

  // Get unique accounts for bar chart
  const getUniqueAccounts = () => {
    const { accounts } = prepareEpBreakdownData();
    return accounts;
  };

  const handleCustomDateChange = ({ startDate, endDate }) => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
  };

  const uniqueAccounts = getUniqueAccounts();
  const pieData = preparePieChartData();
  const { data: barData } = prepareEpBreakdownData();

  const isDataAvailable = pieData.length > 0;

  // Custom tooltip for pie chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const epData = epNoData.find(ep => `EP ${ep.EpNo}` === payload[0].name);
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md">
          <p className="font-semibold" style={{ color: PRIMARY_COLOR }}>
            {payload[0].name}
            {epData && epData.Oem && ` (${epData.Oem})`}
          </p>
          <p className="text-gray-700">{formatCurrency(payload[0].value)}</p>
          <p className="text-gray-500 text-sm">
            {((payload[0].value / costData.totalCost) * 100).toFixed(1)}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for bar chart
  const CustomBarTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const epNo = payload[0]?.payload?.epNo;

      // Find this EP's data in our original dataset to get accurate account costs
      const epData = costData.epBreakdown.find(ep => ep.epNo === epNo);
      const accountCosts = epData?.accountCosts || {};

      // Only show accounts that actually have costs for this EP
      const relevantAccounts = Object.entries(accountCosts)
        .filter(([_, cost]) => cost > 0)
        .sort(([_, costA], [__, costB]) => costB - costA);

      return (
        <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md">
          <p className="font-semibold" style={{ color: PRIMARY_COLOR }}>EP {epNo}</p>
          <p className="font-medium text-gray-700 border-b pb-1 mb-1">
            Total: {formatCurrency(epData?.totalCost || 0)}
          </p>

          {/* Show each account's contribution for this specific EP */}
          {relevantAccounts.map(([account, cost], index) => {
            // Find the color used for this account in the chart
            const accountIndex = uniqueAccounts.findIndex(a => a === account);
            const color = COLORS[accountIndex % COLORS.length];

            return (
              <div key={index} className="flex justify-between items-center text-sm py-1">
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 mr-2 rounded-sm"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-gray-700 truncate max-w-40">{account}</span>
                </div>
                <span className="text-gray-600">{formatCurrency(cost)}</span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Custom rendering for pie chart labels
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={14} fontWeight="bold">
        {pieData[index] ? `${pieData[index].percentage}%` : ""}
      </text>
    );
  };
  // Custom legend for bar chart only
  const CustomLegend = (props) => {
    const { payload } = props;

    return (
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4 text-xs">
        {payload.map((entry, index) => (
          <div key={`legend-${index}`} className="flex items-center">
            <div
              className="w-3 h-3 rounded-full mr-1"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };
  const timeRangeOptions = [
    { value: "1month", label: "Current month" },
    { value: "3months", label: "3 months" },
    { value: "6months", label: "6 months" },
    { value: "1year", label: "1 year" },
    { value: 'custom', label: 'Custom' }
  ];

  // Get current date range display
  const { startDate, endDate } = getDateFilters();

  return (
    <div className="w-full bg-white rounded-lg shadow-sm p-6">
      {epNoData.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="bg-gray-50 rounded-full p-6 mb-4">
            <Filter size={48} className="text-gray-300" />
          </div>
          <h2 className="text-xl font-semibold mb-2" style={{ color: PRIMARY_COLOR }}> No Projects Associated</h2>
          <p className="text-gray-500 max-w-md mx-auto">
            Your account is currently not associated with any projects (EP numbers).
            Please contact your administrator if you believe this is an error.
          </p>
        </div>
      ) : (
        <>
          {/* Header with title and controls */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <h1 className="text-2xl font-semibold" style={{ color: PRIMARY_COLOR }}>
              Cost Analysis by Project
            </h1>

            <div className="flex flex-wrap items-center gap-3">
              {/* Time period selector */}
              <div className="flex border border-gray-200 rounded-md overflow-hidden">
                {timeRangeOptions.map(option => (
                  <button
                    key={option.value}
                    className={`px-3 py-1.5 text-sm transition-colors ${dateRange === option.value
                      ? `text-white`
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    style={{
                      backgroundColor: dateRange === option.value ? PRIMARY_COLOR : undefined
                    }}
                    onClick={() => handleDateRangeChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {dateRange === 'custom' && (
                <div className="w-72">
                  <CustomDateRangePicker
                    startDate={customStartDate}
                    endDate={customEndDate}
                    onChange={handleCustomDateChange}
                    placeholder="Select custom date range"
                  />
                </div>
              )}

              {/* EP filter dropdown */}
              <div className="relative" ref={epDropdownRef}>
                <button
                  className="px-3 py-1.5 border border-gray-300 rounded-md flex items-center gap-2 text-sm bg-white hover:bg-gray-50"
                  onClick={toggleEpDropdown}
                >
                  {selectedEpNos.length} Projects
                  <ChevronDown size={16} />
                </button>

                {showEpDropdown && (
                  <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-300 rounded-lg shadow-md overflow-hidden z-10">
                    {/* Select All option */}
                    <div className="px-3 py-2 hover:bg-gray-50 flex items-center cursor-pointer border-b border-gray-100">
                      <div className="flex justify-between items-center w-full">
                        <div className="flex items-center">
                          <div
                            className={`w-4 h-4 border rounded mr-2 flex items-center justify-center ${epNoData.length > 0 && tempEpSelections.length === epNoData.length ? 'border-transparent' :
                              (tempEpSelections.length > 0 && tempEpSelections.length < epNoData.length ? 'border-transparent' : 'border-gray-300')
                              }`}
                            style={{
                              backgroundColor: epNoData.length > 0 && tempEpSelections.length === epNoData.length ? '#003366' :
                                (tempEpSelections.length > 0 && tempEpSelections.length < epNoData.length ? '#003366' : 'transparent')
                            }}
                            onClick={handleSelectAllEps}
                          >
                            {epNoData.length > 0 && tempEpSelections.length === epNoData.length && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                            {tempEpSelections.length > 0 && tempEpSelections.length < epNoData.length && (
                              <div className="h-1.5 w-1.5 bg-white"></div>
                            )}
                          </div>
                          <span className="font-medium text-sm text-gray-900">Select All</span>
                        </div>
                      </div>
                    </div>

                    {/* Filter options */}
                    <div className="max-h-32 overflow-y-auto">
                      {epNoData.map((ep, idx) => (
                        <div
                          key={ep.id || idx}
                          className="px-3 py-1 hover:bg-gray-50 flex items-center cursor-pointer"
                          onClick={() => toggleEpSelection(ep.EpNo)}
                        >
                          <div
                            className={`w-4 h-4 border rounded mr-2 flex items-center justify-center ${tempEpSelections.includes(ep.EpNo) ? 'border-transparent' : 'border-gray-300'
                              }`}
                            style={{
                              backgroundColor: tempEpSelections.includes(ep.EpNo) ? '#003366' : 'transparent'
                            }}
                          >
                            {tempEpSelections.includes(ep.EpNo) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <span className="truncate text-sm text-gray-700">
                            EP {ep.EpNo} {ep.Oem ? `(${ep.Oem})` : ''}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Apply button */}
                    <div className="border-t border-gray-100 p-2 flex justify-end bg-white">
                      <button
                        className="text-white py-1 px-3 text-sm rounded-lg transition-colors"
                        style={{ backgroundColor: '#003366' }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#004080';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#003366';
                        }}
                        onClick={applyEpFilter}
                        disabled={tempEpSelections.length === 0}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Export button */}
              <div className="relative group">
                <button
                  className="px-3 py-1.5 border border-gray-300 rounded-md flex items-center gap-2 text-sm bg-white hover:bg-gray-50 transition-colors"
                  onClick={() => exportCostExplorerDataToCsv(prepareExportData(), `project-costs-${startDate}-to-${endDate}.csv`)}
                >
                  <Download size={16} />
                </button>
                <div
                  className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap"
                  style={{ backgroundColor: '#003366' }}
                >
                  Export as CSV
                </div>
              </div>
            </div>
          </div>

          {/* Total cost summary */}
          <div className="mb-8">
            <div className="flex items-baseline">
              <h2 className="text-3xl font-bold" style={{ color: PRIMARY_COLOR }}>
                {formatCurrency(costData.totalCost)}
              </h2>
              <span className="ml-2 text-gray-600 text-sm">total cost for selected Projects</span>
            </div>
            <p className="text-sm text-gray-500">
              Data for the period: {startDate} to {endDate}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Cost distribution pie chart */}
            <div className="w-full relative">
              <h3 className="text-lg font-medium mb-4" style={{ color: PRIMARY_COLOR }}>Project Cost Distribution</h3>
              <div className="h-80 relative">
                {(loading || isUpdating) && (
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] z-10 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: PRIMARY_COLOR }}></div>
                      <span className="text-sm text-gray-600">Updating data...</span>
                    </div>
                  </div>
                )}
                <ResponsiveContainer width="100%" height="100%">
                  {isDataAvailable ? (
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                        animationBegin={0}
                        animationDuration={800}
                        isAnimationActive={dataReady && !isUpdating}
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        iconType="circle"
                        wrapperStyle={{
                          paddingTop: '20px',
                          fontSize: '12px'
                        }}
                        formatter={(value, entry) => {
                          const epData = epNoData.find(ep => `EP ${ep.EpNo}` === value);
                          return (
                            <span style={{ color: '#374151', fontSize: '12px' }}>
                              {value}{epData && epData.Oem ? ` (${epData.Oem})` : ''}
                            </span>
                          );
                        }}
                      />
                    </PieChart>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center text-gray-400">
                        No data available
                      </div>
                    </div>
                  )}
                </ResponsiveContainer>
              </div>
            </div>

            {/* EP breakdown bar chart */}
            <div className="w-full relative">
              <h3 className="text-lg font-medium mb-4" style={{ color: PRIMARY_COLOR }}>Top Projects by Cost</h3>
              <div className="h-80 relative">
                {(loading || isUpdating) && (
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-[1px] z-10 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: PRIMARY_COLOR }}></div>
                      <span className="text-sm text-gray-600">Updating data...</span>
                    </div>
                  </div>
                )}
                <ResponsiveContainer width="100%" height="100%">
                  {isDataAvailable ? (
                    <BarChart
                      data={barData}
                      margin={{ top: 20, right: 20, left: 0, bottom: 30 }}
                      animationBegin={0}
                      animationDuration={800}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis
                        dataKey="epNo"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `EP ${value}`}
                        axisLine={{ stroke: '#e0e0e0' }}
                      />
                      <YAxis
                        tickFormatter={(value) => `$${value}`}
                        tick={{ fontSize: 12 }}
                        axisLine={{ stroke: '#e0e0e0' }}
                      />
                      <Tooltip content={<CustomBarTooltip />} />
                      <Legend
                        payload={uniqueAccounts.map((account, index) => ({
                          value: account,
                          type: 'square',
                          color: COLORS[index % COLORS.length]
                        }))}
                        content={<CustomLegend />}
                      />
                      {uniqueAccounts.map((account, index) => (
                        <Bar
                          key={account}
                          dataKey={account}
                          stackId="a"
                          fill={COLORS[index % COLORS.length]}
                          isAnimationActive={dataReady && !isUpdating}
                        />
                      ))}
                    </BarChart>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center text-gray-400">
                        No data available
                      </div>
                    </div>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Toast message */}
          {toast && (
            <div className={`fixed bottom-4 right-4 px-4 py-2 rounded-md shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
              }`}>
              {toast.message}
              <button
                className="ml-2 text-white"
                onClick={() => setToast(null)}
              >
                ×
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AllEpDataGraph;